import L from "leaflet";
import { Actions, HidroLayer, HidroLayerGroup } from "./layers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function exportGeoJSON(geoJSON: any, filename: string) {
  if (filename.endsWith(".json")) {
    filename.replaceAll(".json", ".geojson");
  } else if (!filename.endsWith(".geojson")) {
    filename += ".geojson";
  }

  const dataStr = JSON.stringify(geoJSON, null, 2);
  const blob = new Blob([dataStr], { type: 'application/json' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;

  link.click();
  URL.revokeObjectURL(link.href);
}

export const geometryParser = (geom: GeoJSON.GeoJsonObject, actions?: Actions, name?: string) => {
  if (geom.type.startsWith("Multi") || ["Point", "LineString", "Polygon"].includes(geom.type)) {
    const geo = geom as GeoJSON.MultiLineString | GeoJSON.MultiPoint | GeoJSON.MultiPolygon | GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon;
    return processGeom(geo, undefined, actions, name);
  }

  if (geom.type === "Feature") {
    const geo = geom as GeoJSON.Feature;
    return processFeature(geo, actions, name);
  }

  // Feature Collection
  const geo = geom as GeoJSON.FeatureCollection;

  return new HidroLayerGroup({
    name: name ?? "Grupo",
    showing: true,
    layers: geo.features.map((geom) => processFeature(geom, actions)),
  });
};

const processFeature = (geom?: GeoJSON.Feature, actions?: Actions, name?: string) => {
  return processGeom(geom?.geometry, geom?.properties, actions, name);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processGeom(geom?: GeoJSON.Geometry, properties?: any, actions?: Actions, name?: string): (HidroLayer | HidroLayerGroup) {
  if (geom && geom.type === "GeometryCollection") {
    return new HidroLayerGroup({
      name: name ?? properties?.name ?? properties?.id ?? "Geometrias",
      layers: geom.geometries.map(geo => processGeom(geo, properties, actions)),
      showing: true,
    });
  }

  if (geom && geom.type.startsWith("Multi")) {
    return processMulti(geom as GeoJSON.MultiLineString | GeoJSON.MultiPoint | GeoJSON.MultiPolygon, properties, actions, name);
  }
  if (geom && ["Point", "LineString", "Polygon"].includes(geom.type)) {
    return processUnique(geom as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon, properties, actions, name);
  }

  return new HidroLayerGroup({
    name: "Erro ao Ler Geometria",
    showing: true,
    layers: [],
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processUnique = (geom: GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon, properties: any, actions?: Actions, name?: string) => {
  let layer: L.Marker | L.Polyline | L.Polygon = new L.Marker(new L.LatLng(0, 0));

  if (geom.type === "Point") {
    const geo = geom as GeoJSON.Point;
    layer = new L.Marker(invertCoords(geo.coordinates) as L.LatLng);
  }
  if (geom.type === "LineString") {
    const geo = geom as GeoJSON.LineString;
    layer = new L.Polyline(invertCoords(geo.coordinates) as (L.LatLng[] | L.LatLng[][])) as L.Polyline;
  }
  if (geom.type === "Polygon") {
    const geo = geom as GeoJSON.Polygon;
    layer = new L.Polygon(invertCoords(geo.coordinates) as (L.LatLng[] | L.LatLng[][] | L.LatLng[][][]));
  }

  return new HidroLayer({
    layer: layer,
    name: name ?? properties?.name ?? geom.type,
    showing: true,
    actions: actions,
    properties: properties,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processMulti = (geom: GeoJSON.MultiLineString | GeoJSON.MultiPoint | GeoJSON.MultiPolygon, properties: any, actions?: Actions, name?: string) => {
  if (geom.type === "MultiPoint") {
    const geo = geom as GeoJSON.MultiPoint;

    return new HidroLayer({
      name: name ?? properties?.name ?? geom.type,
      showing: true,
      layer: new L.LayerGroup(geo.coordinates.map(coord => new L.Marker(invertCoords(coord) as L.LatLng))),
      actions: actions,
      properties: properties
    });
  }
  if (geom.type === "MultiLineString") {
    const geo = geom as GeoJSON.MultiLineString;
    return new HidroLayer({
      name: name ?? properties?.name ?? geom.type,
      showing: true,
      layer: new L.LayerGroup(geo.coordinates.map(coord => new L.Polyline(invertCoords(coord) as L.LatLng[] | L.LatLng[][]))),
      actions: actions,
      properties: properties
    });
  }
  else { // (geom.type === "MultiPolygon") {
    const geo = geom as GeoJSON.MultiPolygon;
    return new HidroLayer({
      name: name ?? properties?.name ?? geom.type,
      showing: true,
      layer: new L.LayerGroup(geo.coordinates.map(coord => new L.Polygon(invertCoords(coord) as L.LatLng[] | L.LatLng[][] | L.LatLng[][][]))),
      actions: actions,
      properties: properties
    });
  }
};

type CoordTypes = GeoJSON.Position | GeoJSON.Position[] | GeoJSON.Position[][] | GeoJSON.Position[][][];
function invertCoords(coords: CoordTypes): L.LatLng | L.LatLng[] | L.LatLng[][] | L.LatLng[][][] {

  const isPosition = Array.isArray(coords) && coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number';
  if (isPosition) {
    const coord = coords as GeoJSON.Position;
    return new L.LatLng(coord[1], coord[0], coord.length > 2 ? coord[2] : undefined);
  }

  const coord = coords as GeoJSON.Position[] | GeoJSON.Position[][] | GeoJSON.Position[][][];
  const a = coord.map(coord => invertCoords(coord)) as L.LatLng[] | L.LatLng[][] | L.LatLng[][][];
  return a;
}